import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Plus, 
  BarChart3,
  Loader2,
  TrendingUp,
  Globe,
  Link,
  Search,
  Target
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEODashboard = () => {
  const { user, getAuthHeaders } = useAuth();
  const [projects, setProjects] = useState([]);
  const [seoData, setSeoData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState('');
  const [formData, setFormData] = useState({
    project_id: '',
    keywords: '',
    domain_authority: '',
    page_authority: '',
    organic_traffic: '',
    backlinks: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, seoRes] = await Promise.all([
        axios.get(`${API_URL}/projects`, getAuthHeaders()),
        axios.get(`${API_URL}/seo-data`, getAuthHeaders())
      ]);
      setProjects(projectsRes.data);
      setSeoData(seoRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        keywords: formData.keywords.split(',').map(k => k.trim()).filter(k => k),
        domain_authority: formData.domain_authority ? parseFloat(formData.domain_authority) : null,
        page_authority: formData.page_authority ? parseFloat(formData.page_authority) : null,
        organic_traffic: formData.organic_traffic ? parseInt(formData.organic_traffic) : null,
        backlinks: formData.backlinks ? parseInt(formData.backlinks) : null
      };
      
      await axios.post(`${API_URL}/seo-data`, data, getAuthHeaders());
      toast.success('SEO data added successfully');
      setIsCreateOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add SEO data');
    }
  };

  const resetForm = () => {
    setFormData({
      project_id: '',
      keywords: '',
      domain_authority: '',
      page_authority: '',
      organic_traffic: '',
      backlinks: '',
      notes: ''
    });
  };

  // Aggregate stats
  const totalKeywords = seoData.reduce((acc, d) => acc + (d.keywords?.length || 0), 0);
  const avgDA = seoData.filter(d => d.domain_authority).reduce((acc, d, _, arr) => acc + d.domain_authority / arr.length, 0);
  const totalTraffic = seoData.reduce((acc, d) => acc + (d.organic_traffic || 0), 0);
  const totalBacklinks = seoData.reduce((acc, d) => acc + (d.backlinks || 0), 0);

  // Chart data
  const chartData = seoData.slice(0, 10).map((d, i) => ({
    name: projects.find(p => p.id === d.project_id)?.name?.slice(0, 15) || `Project ${i + 1}`,
    da: d.domain_authority || 0,
    pa: d.page_authority || 0,
    traffic: d.organic_traffic || 0,
    backlinks: d.backlinks || 0
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="seo-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            SEO Analytics
          </h1>
          <p className="text-slate-500 mt-1">Track keyword rankings and site performance</p>
        </div>
        
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-seo-data-btn">
              <Plus size={18} className="mr-2" />
              Add SEO Data
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>Add SEO Data</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Project *</Label>
                <Select
                  value={formData.project_id}
                  onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                >
                  <SelectTrigger data-testid="seo-project-select">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(project => (
                      <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Keywords (comma separated)</Label>
                <Input
                  value={formData.keywords}
                  onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                  placeholder="seo services, web design, digital marketing"
                  data-testid="seo-keywords-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Domain Authority (0-100)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.domain_authority}
                    onChange={(e) => setFormData({ ...formData, domain_authority: e.target.value })}
                    placeholder="45"
                    data-testid="seo-da-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Page Authority (0-100)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.page_authority}
                    onChange={(e) => setFormData({ ...formData, page_authority: e.target.value })}
                    placeholder="40"
                    data-testid="seo-pa-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Organic Traffic</Label>
                  <Input
                    type="number"
                    value={formData.organic_traffic}
                    onChange={(e) => setFormData({ ...formData, organic_traffic: e.target.value })}
                    placeholder="5000"
                    data-testid="seo-traffic-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Backlinks</Label>
                  <Input
                    type="number"
                    value={formData.backlinks}
                    onChange={(e) => setFormData({ ...formData, backlinks: e.target.value })}
                    placeholder="150"
                    data-testid="seo-backlinks-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional SEO notes..."
                  rows={3}
                  data-testid="seo-notes-input"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-seo-btn">
                  Save Data
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
                <Target size={24} className="text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{totalKeywords}</p>
                <p className="text-sm text-slate-500">Total Keywords</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-emerald-50 flex items-center justify-center">
                <Globe size={24} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{avgDA.toFixed(1)}</p>
                <p className="text-sm text-slate-500">Avg Domain Authority</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-purple-50 flex items-center justify-center">
                <TrendingUp size={24} className="text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{totalTraffic.toLocaleString()}</p>
                <p className="text-sm text-slate-500">Total Organic Traffic</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-amber-50 flex items-center justify-center">
                <Link size={24} className="text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{totalBacklinks.toLocaleString()}</p>
                <p className="text-sm text-slate-500">Total Backlinks</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900">Domain & Page Authority</CardTitle>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="da" fill="#0f172a" name="Domain Authority" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="pa" fill="#3b82f6" name="Page Authority" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400">
                No SEO data available
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900">Traffic & Backlinks</CardTitle>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="traffic" stroke="#10b981" strokeWidth={2} name="Traffic" />
                  <Line type="monotone" dataKey="backlinks" stroke="#f59e0b" strokeWidth={2} name="Backlinks" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400">
                No SEO data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* SEO Data Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900">SEO Data by Project</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {seoData.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Keywords</th>
                    <th>DA</th>
                    <th>PA</th>
                    <th>Traffic</th>
                    <th>Backlinks</th>
                  </tr>
                </thead>
                <tbody>
                  {seoData.map((data) => {
                    const project = projects.find(p => p.id === data.project_id);
                    return (
                      <tr key={data.id}>
                        <td className="font-medium text-slate-900">{project?.name || 'Unknown'}</td>
                        <td>
                          <div className="flex flex-wrap gap-1">
                            {data.keywords?.slice(0, 3).map((kw, i) => (
                              <span key={i} className="px-2 py-0.5 bg-slate-100 rounded text-xs">
                                {kw}
                              </span>
                            ))}
                            {data.keywords?.length > 3 && (
                              <span className="text-xs text-slate-400">+{data.keywords.length - 3}</span>
                            )}
                          </div>
                        </td>
                        <td>{data.domain_authority || '-'}</td>
                        <td>{data.page_authority || '-'}</td>
                        <td>{data.organic_traffic?.toLocaleString() || '-'}</td>
                        <td>{data.backlinks?.toLocaleString() || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center">
              <BarChart3 size={48} className="mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-700">No SEO data yet</h3>
              <p className="text-slate-500 mt-1">Add SEO metrics to track your projects' performance</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SEODashboard;
